import { nextTick } from 'vue'
import type { App } from 'vue'
import * as elIcons from '@element-plus/icons-vue'
import router from '/@/router/index'
import Icon from '/@/components/icon/index.vue'
import { useNavTabs } from '/@/stores/navTabs'
import { useMemberCenter } from '/@/stores/memberCenter'
import type { FormInstance } from 'element-plus'
import { useSiteConfig } from '../stores/siteConfig'
import { useTitle } from '@vueuse/core'
import { i18n } from '../lang'
import { getUrl } from './axios'
import { adminBaseRoutePath } from '/@/router/static/adminBase'
import { isArray, trim, trimStart } from 'lodash-es'
import type { TranslateOptions } from 'vue-i18n'
import { ElMessage } from "element-plus"
import {decimal} from "/@/utils/decimal";

export function registerIcons(app: App) {
    /*
     * 全局注册 Icon
     * 使用方式: <Icon name="name" size="size" color="color" />
     * 详见<待完善>
     */
    app.component('Icon', Icon)

    /*
     * 全局注册element Plus的icon
     */
    const icons = elIcons as any
    for (const i in icons) {
        app.component(`el-icon-${icons[i].name}`, icons[i])
    }
}

/**
 * 加载网络css文件
 * @param url css资源url
 */
export function loadCss(url: string): void {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = url
    link.crossOrigin = 'anonymous'
    document.getElementsByTagName('head')[0].appendChild(link)
}

/**
 * 加载网络js文件
 * @param url js资源url
 */
export function loadJs(url: string): void {
    const link = document.createElement('script')
    link.src = url
    document.body.appendChild(link)
}

/**
 * 根据路由 meta.title 设置浏览器标题
 */
export function setTitleFromRoute() {
    nextTick(() => {
        if (typeof router.currentRoute.value.meta.title != 'string') {
            return
        }
        const webTitle = i18n.global.te(router.currentRoute.value.meta.title)
            ? i18n.global.t(router.currentRoute.value.meta.title)
            : router.currentRoute.value.meta.title
        const title = useTitle()
        const siteConfig = useSiteConfig()
        title.value = `${webTitle}${siteConfig.siteName ? ' - ' + siteConfig.siteName : ''}`
    })
}

/**
 * 设置浏览器标题
 * @param webTitle 新的标题
 */
export function setTitle(webTitle: string) {
    if (router.currentRoute.value) {
        router.currentRoute.value.meta.title = webTitle
    }
    nextTick(() => {
        const title = useTitle()
        const siteConfig = useSiteConfig()
        title.value = `${webTitle}${siteConfig.siteName ? ' - ' + siteConfig.siteName : ''}`
    })
}

/**
 * 是否是外部链接
 * @param path
 */
export function isExternal(path: string): boolean {
    return /^(https?|ftp|mailto|tel):/.test(path)
}

/**
 * 全局防抖
 * 与 _.debounce 不同的是，间隔期间如果再次传递不同的函数，两个函数也只会执行一次
 * @param fn 执行函数
 * @param ms 间隔毫秒数
 */
export const debounce = (fn: Function, ms: number) => {
    return (...args: any[]) => {
        if (window.lazy) {
            clearTimeout(window.lazy)
        }
        window.lazy = window.setTimeout(() => {
            fn(...args)
        }, ms)
    }
}

/**
 * 根据pk字段的值从数组中获取key
 * @param arr
 * @param pk
 * @param value
 */
export const getArrayKey = (arr: any, pk: string, value: any): any => {
    for (const key in arr) {
        if (arr[key][pk] == value) {
            return key
        }
    }
    return false
}

/**
 * 表单重置
 * @param formEl
 */
export const onResetForm = (formEl: FormInstance | undefined) => {
    if (!formEl) return
    formEl.resetFields && formEl.resetFields()
}

/**
 * 将数据构建为ElTree的data {label:'', children: []}
 * @param data
 */
export const buildJsonToElTreeData = (data: any): ElTreeData[] => {
    if (typeof data == 'object') {
        const childrens = []
        for (const key in data) {
            childrens.push({
                label: key + ': ' + data[key],
                children: buildJsonToElTreeData(data[key]),
            })
        }
        return childrens
    } else {
        return []
    }
}

/**
 * 是否在后台应用内
 * @param path 不传递则通过当前路由 path 检查
 */
export const isAdminApp = (path = '') => {
    const regex = new RegExp(`^${adminBaseRoutePath}`)
    if (path) {
        return regex.test(path)
    }
    if (regex.test(getCurrentRoutePath())) {
        return true
    }
    return false
}

/**
 * 是否为手机设备
 */
export const isMobile = () => {
    return !!navigator.userAgent.match(
        /android|webos|ip(hone|ad|od)|opera (mini|mobi|tablet)|iemobile|windows.+(phone|touch)|mobile|fennec|kindle (Fire)|Silk|maemo|blackberry|playbook|bb10\; (touch|kbd)|Symbian(OS)|Ubuntu Touch/i
    )
}

/**
 * 从一个文件路径中获取文件名
 * @param path 文件路径
 */
export const getFileNameFromPath = (path: string) => {
    const paths = path.split('/')
    return paths[paths.length - 1]
}

export function auth(node: string): boolean
export function auth(node: { name: string; subNodeName?: string }): boolean

/**
 * 鉴权
 * 提供 string 将根据当前路由 path 自动拼接和鉴权，还可以提供路由的 name 对象进行鉴权
 * @param node
 */
export function auth(node: string | { name: string; subNodeName?: string }) {
    const store = isAdminApp() ? useNavTabs() : useMemberCenter()
    if (typeof node === 'string') {
        const path = getCurrentRoutePath()
        if (store.state.authNode.has(path)) {
            const subNodeName = path + (path == '/' ? '' : '/') + node
            if (store.state.authNode.get(path)!.some((v: string) => v == subNodeName)) {
                return true
            }
        }
    } else {
        // 节点列表中没有找到 name
        if (!node.name || !store.state.authNode.has(node.name)) return false

        // 无需继续检查子节点或未找到子节点
        if (!node.subNodeName || store.state.authNode.get(node.name)?.includes(node.subNodeName)) return true
    }
    return false
}

/**
 * 获取资源完整地址
 * @param relativeUrl 资源相对地址
 * @param domain 指定域名
 */
export const fullUrl = (relativeUrl: string, domain = '') => {
    const siteConfig = useSiteConfig()
    if (!domain) {
        domain = siteConfig.cdnUrl ? siteConfig.cdnUrl : getUrl()
    }
    if (!relativeUrl) return domain

    const regUrl = new RegExp(/^http(s)?:\/\//)
    const regexImg = new RegExp(/^((?:[a-z]+:)?\/\/|data:image\/)(.*)/i)
    if (!domain || regUrl.test(relativeUrl) || regexImg.test(relativeUrl)) {
        return relativeUrl
    }
    return domain + relativeUrl
}

/**
 * 获取路由 path
 */
export const getCurrentRoutePath = () => {
    let path = router.currentRoute.value.path
    if (path == '/') path = trimStart(window.location.hash, '#')
    if (path.indexOf('?') !== -1) path = path.replace(/\?.*/, '')
    return path
}

/**
 * 获取根据当前路由路径动态加载的语言翻译
 * @param key 无需语言路径的翻译key，亦可使用完整路径
 * @param named — 命名插值的值
 * @param options — 其他翻译选项
 * @returns — Translated message
 */
export const __ = (key: string, named?: Record<string, unknown>, options?: TranslateOptions<string>) => {
    let langPath = ''
    const path = getCurrentRoutePath()
    if (isAdminApp()) {
        langPath = path.slice(path.indexOf(adminBaseRoutePath) + adminBaseRoutePath.length)
        langPath = trim(langPath, '/').replaceAll('/', '.')
    } else {
        langPath = trim(path, '/').replaceAll('/', '.')
    }
    langPath = langPath ? langPath + '.' + key : key
    return i18n.global.te(langPath) ? i18n.global.t(langPath, named ?? {}, options) : i18n.global.t(key, named ?? {}, options)
}

/**
 * 文件类型效验，前端根据服务端配置进行初步检查
 * @param fileName 文件名
 * @param fileType 文件 mimeType，不一定存在
 */
export const checkFileMimetype = (fileName: string, fileType: string) => {
    if (!fileName) return false
    const siteConfig = useSiteConfig()
    const allowedSuffixes = isArray(siteConfig.upload.allowedSuffixes)
        ? siteConfig.upload.allowedSuffixes
        : siteConfig.upload.allowedSuffixes.toLowerCase().split(',')

    const allowedMimeTypes = isArray(siteConfig.upload.allowedMimeTypes)
        ? siteConfig.upload.allowedMimeTypes
        : siteConfig.upload.allowedMimeTypes.toLowerCase().split(',')

    const fileSuffix = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase()
    if (allowedSuffixes.includes(fileSuffix) || allowedSuffixes.includes('.' + fileSuffix)) {
        return true
    }
    if (fileType && allowedMimeTypes.includes(fileType)) {
        return true
    }
    return false
}

/**
 * 获取一组资源的完整地址
 * @param relativeUrls 资源相对地址
 * @param domain 指定域名
 */
export const arrayFullUrl = (relativeUrls: string | string[], domain = '') => {
    if (typeof relativeUrls === 'string') {
        relativeUrls = relativeUrls == '' ? [] : relativeUrls.split(',')
    }
    for (const key in relativeUrls) {
        relativeUrls[key] = fullUrl(relativeUrls[key], domain)
    }
    return relativeUrls
}

/**
 * 格式化时间戳
 * @param dateTime 时间戳
 * @param fmt 格式化方式，默认：yyyy-mm-dd hh:MM:ss
 */
export const timeFormat = (dateTime: string | number | null = null, fmt = 'yyyy-mm-dd hh:MM:ss') => {
    if (dateTime == 'none') return i18n.global.t('None')
    if (!dateTime) dateTime = Number(new Date())
    if (dateTime.toString().length === 10) {
        dateTime = +dateTime * 1000
    }

    const date = new Date(dateTime)
    let ret
    const opt: anyObj = {
        'y+': date.getFullYear().toString(), // 年
        'm+': (date.getMonth() + 1).toString(), // 月
        'd+': date.getDate().toString(), // 日
        'h+': date.getHours().toString(), // 时
        'M+': date.getMinutes().toString(), // 分
        's+': date.getSeconds().toString(), // 秒
    }
    for (const k in opt) {
        ret = new RegExp('(' + k + ')').exec(fmt)
        if (ret) {
            fmt = fmt.replace(ret[1], ret[1].length == 1 ? opt[k] : padStart(opt[k], ret[1].length, '0'))
        }
    }
    return fmt
}

/**
 * 字符串补位
 */
const padStart = (str: string, maxLength: number, fillString = ' ') => {
    if (str.length >= maxLength) return str

    const fillLength = maxLength - str.length
    let times = Math.ceil(fillLength / fillString.length)
    while ((times >>= 1)) {
        fillString += fillString
        if (times === 1) {
            fillString += fillString
        }
    }
    return fillString.slice(0, fillLength) + str
}

/**
 * 根据当前时间生成问候语
 */
export const getGreet = () => {
    const now = new Date()
    const hour = now.getHours()
    let greet = ''

    if (hour < 5) {
        greet = i18n.global.t('utils.Late at night, pay attention to your body!')
    } else if (hour < 9) {
        greet = i18n.global.t('utils.good morning!') + i18n.global.t('utils.welcome back')
    } else if (hour < 12) {
        greet = i18n.global.t('utils.Good morning!') + i18n.global.t('utils.welcome back')
    } else if (hour < 14) {
        greet = i18n.global.t('utils.Good noon!') + i18n.global.t('utils.welcome back')
    } else if (hour < 18) {
        greet = i18n.global.t('utils.good afternoon') + i18n.global.t('utils.welcome back')
    } else if (hour < 24) {
        greet = i18n.global.t('utils.Good evening') + i18n.global.t('utils.welcome back')
    } else {
        greet = i18n.global.t('utils.Hello!') + i18n.global.t('utils.welcome back')
    }
    return greet
}

/**
 * 获取最近的时间区间 如今天，近3天
 */
export const getDateInterval = (days: number | 'today' | 'week' | 'month' | 'yesterday', endDate?: Date) => {
    const DAY = 24 * 60 * 60 * 1000
    /**
     * 获取今天的开始和结束时间
     */
    const getTodayStartAndEnd = () => {
        // 获取今天的时间 从 yyyy-mm-dd 00:00:00 - 当前
        const start = new Date()
        start.setHours(0, 0, 0, 0)
        const end = endDate ?? new Date()
        end.setHours(23, 59, 59, 999)
        return [timeFormat(start as unknown as string), timeFormat(end as unknown as string)]
    }
    /**
     * 获取昨天的开始和结束时间
     */
    const getYesterdayStartAndEnd = () => {
        const end = new Date()
        end.setDate(end.getDate() - 1)
        end.setHours(23, 59, 59, 999)

        const start = new Date(end)
        start.setHours(0, 0, 0, 0)
        end.setHours(23, 59, 59, 999)
        return [timeFormat(start as unknown as string), timeFormat(end as unknown as string)]
    }
    /**
     * 获取当前周的开始和结束时间
     */
    const getWeekStartAndEnd = () => {
        const end = endDate ?? new Date()
        const dayOfWeek = end.getDay()
        // 周日是 0，这里转换为 7
        const adjustedDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek
        const start = new Date(end.getTime() - adjustedDayOfWeek * DAY)
        start.setHours(0, 0, 0, 0)
        end.setHours(23, 59, 59, 999)
        return [timeFormat(start as unknown as string), timeFormat(end as unknown as string)]
    }

    /**
     * 获取当前月的开始和结束时间
     */
    const getMonthStartAndEnd = () => {
        const end = endDate ?? new Date()
        const start = new Date(end)
        start.setDate(1)
        start.setHours(0, 0, 0, 0)
        end.setHours(23, 59, 59, 999)
        return [timeFormat(start as unknown as string), timeFormat(end as unknown as string)]
    }

    if (typeof days === 'string') {
        let result = []
        switch (days) {
            case 'today':
                result = getTodayStartAndEnd()
                break
            case 'yesterday':
                result = getYesterdayStartAndEnd()
                break
            case 'week':
                result = getWeekStartAndEnd()
                break
            case 'month':
                result = getMonthStartAndEnd()
                break
        }
        return result
    } else {
        const endTime = endDate ? new Date(endDate) : new Date()
        // 根据结束时间，按照时间戳计算出开始时间
        const startTime = new Date(endTime.getTime() - (days - 1) * DAY)
        // 结束时间：当天 23:59:59.999
        endTime.setHours(23, 59, 59, 999)
        startTime.setHours(0, 0, 0, 0)
        return [timeFormat(startTime.toString()), timeFormat(endTime.toString())]
    }
}
/**
 * 数字转人民币大写
 */
export function smalltoBIG(n: number) {
    if (isNaN(n)) return ''
    if (n < 0) return ''

    const fraction = ['角', '分']
    const digit = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
    const unit = [
        ['元', '万', '亿'],
        ['', '拾', '佰', '仟'],
    ]

    let s = ''

    // ---- 修复：使用字符串避免浮点误差 ----
    // const str = n.toFixed(2) // 始终保留两位小数
    const str = Number(n).toFixed(2) // 始终保留两位小数
    const [intPartStr, decPartStr] = str.split('.')

    // 小数部分（角、分）
    for (let i = 0; i < fraction.length; i++) {
        const d = decPartStr[i]
        if (d && d !== '0') {
            s += digit[Number(d)] + fraction[i]
        }
    }
    s = s || '整'

    // 整数部分
    let intPart = Number(intPartStr)
    for (let i = 0; i < unit[0].length && intPart > 0; i++) {
        let p = ''
        for (let j = 0; j < unit[1].length && intPart > 0; j++) {
            p = digit[intPart % 10] + unit[1][j] + p
            intPart = Math.floor(intPart / 10)
        }
        s = p.replace(/(零.)*零$/, '').replace(/^$/, '零') + unit[0][i] + s
    }

    return s
        .replace(/(零.)*零元/, '元')
        .replace(/(零.)+/g, '零')
        .replace(/^整$/, '零元整')
}
/**
 * 人民币格式化
 * @param amount 金额
 * @param withSymbol 是否显示人民币符号，默认 true
 */
// export function formatRMB(amount: string | number, withSymbol: boolean = true) {
//     const _amount = Number(amount)
//     if (isNaN(_amount)) return '0.00'
//
//     if (withSymbol) {
//         return new Intl.NumberFormat('zh-CN', {
//             style: 'currency',
//             currency: 'CNY',
//         }).format(_amount)
//     } else {
//         return new Intl.NumberFormat('zh-CN', {
//             minimumFractionDigits: 2,
//             maximumFractionDigits: 2,
//         }).format(_amount)
//     }
// }

/**
 * 人名打码：保留首尾，中间用*代替
 * @param name 人名
 * @returns 打码后的字符串
 */
export function maskName(name: string | null | undefined): string {
    if (!name) return '--'
    const s = String(name).trim()
    if (s.length <= 1) return s
    if (s.length === 2) return s[0] + '*'
    return s[0] + '*'.repeat(s.length - 2) + s[s.length - 1]
}

/**
 * 电话打码：保留前3后4，中间用*代替
 * @param phone 电话/手机号
 * @returns 打码后的字符串
 */
export function maskPhone(phone: string | null | undefined): string {
    if (!phone) return '--'
    const s = String(phone).trim()
    if (s.length <= 3) return '*'.repeat(s.length)
    if (s.length <= 7) return s.slice(0, 2) + '*'.repeat(s.length - 2)
    return s.slice(0, 3) + '****' + s.slice(-4)
}

/**
 * 邮箱打码：保留首字符和@及域名，中间用*代替
 * @param email 邮箱
 * @returns 打码后的字符串
 */
export function maskEmail(email: string | null | undefined): string {
    if (!email) return '--'
    const s = String(email).trim()
    const at = s.indexOf('@')
    if (at <= 0) return maskAccount(s)
    return s[0] + '****' + s.slice(at)
}

/**
 * 银行卡号/银行账号打码：仅保留后4位
 * @param cardNo 银行卡号
 * @returns 打码后的字符串
 */
export function maskBankCard(cardNo: string | null | undefined): string {
    if (!cardNo) return '--'
    const s = String(cardNo).trim()
    if (s.length <= 4) return '*'.repeat(s.length)
    return '**** **** ' + s.slice(-4)
}

/**
 * 身份证号打码：保留前1后1，中间用*代替
 * @param idCard 身份证号
 * @returns 打码后的字符串
 */
export function maskIdCard(idCard: string | null | undefined): string {
    if (!idCard) return '--'
    const s = String(idCard).trim()
    if (s.length <= 2) return '*'.repeat(s.length)
    return s[0] + '*'.repeat(s.length - 2) + s[s.length - 1]
}

/**
 * 通用编号打码（用户名/商编/商户号等）：保留前2后2，中间用*代替
 * @param code 编号
 * @returns 打码后的字符串
 */
export function maskAccount(code: string | null | undefined): string {
    if (!code) return '--'
    const s = String(code).trim()
    if (s.length <= 4) return '*'.repeat(s.length)
    return s.slice(0, 2) + '*'.repeat(s.length - 4) + s.slice(-2)
}

/**
 * 格式化人民币金额
 * @param amount 数值或字符串
 * @param withSymbol 是否带上人民币符号，默认 true
 * @returns 格式化后的字符串
 */
export function formatRMB(amount: string | number, withSymbol: boolean = true): string {
    const num = Number(amount)
    if (isNaN(num)) return withSymbol ? '¥0.00' : '0.00'

    // 保留两位小数，转为字符串
    const fixed = decimal.toFixed(num,2)

    // 拆分整数部分和小数部分
    const [intPart, decimalPart] = fixed.split('.')

    // 给整数部分加千分位
    const intWithComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    // 拼接结果
    return (withSymbol ? '¥' : '') + intWithComma + '.' + decimalPart
}

/**
 * 驼峰转下划线
 * @param str - 输入的驼峰字符串
 * @returns 下划线风格字符串
 */
export const camelToSnake = (str: string): string => {
    return str
        .replace(/([A-Z])/g, '_$1') // 大写字母前加 _
        .toLowerCase() // 转小写
        .replace(/^_/, '') // 去掉开头的 _
}

/**
 * 下划线转驼峰
 * @param str - 输入的下划线字符串
 * @returns 驼峰风格字符串
 */
export const snakeToCamel = (str: string): string => {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * 计算借款到期日
 * @param startDate 借款日期
 * @param months 借款期限（月数）
 * @returns 到期日（Date对象）
 */
export function calculateDueDate(startDate: Date, months: number | string): Date {
    const monthsInt = Number(months)
    if (!Number.isFinite(monthsInt) || monthsInt < 0 || Math.floor(monthsInt) !== monthsInt) {
        throw new Error('months must be a non-negative integer (or numeric string).')
    }

    const year_S = startDate.getFullYear()
    const month_S = startDate.getMonth() + 1 // 1-based
    const day_S = startDate.getDate()

    const expireMonth = ((month_S + monthsInt - 1) % 12) + 1
    const expireYear = year_S + Math.floor((month_S + monthsInt - 1) / 12)

    const lastDay = new Date(expireYear, expireMonth, 0).getDate()
    const expireDay = day_S <= lastDay ? day_S : lastDay

    return new Date(expireYear, expireMonth - 1, expireDay)
}

/**
 * 根据时间戳转换多久前
 */
export const timeAgo = (timestamp: any): any => {
    const now = Date.now()
    const secondsAgo = Math.floor((now - timestamp) / 1000)
    const minutesAgo = Math.floor(secondsAgo / 60)
    const hoursAgo = Math.floor(minutesAgo / 60)
    const daysAgo = Math.floor(hoursAgo / 24)
    const weeksAgo = Math.floor(daysAgo / 7)
    const monthsAgo = Math.floor(daysAgo / 30) // 注意这里的月份计算简化处理，实际应用中可能需要更精确的计算
    const yearsAgo = Math.floor(daysAgo / 365)

    if (yearsAgo > 0) return `${yearsAgo}年前`
    if (monthsAgo > 0) return `${monthsAgo}个月前`
    if (weeksAgo > 0) return `${weeksAgo}周前`
    if (daysAgo > 0) return `${daysAgo}天前`
    if (hoursAgo > 0) return `${hoursAgo}小时前`
    if (minutesAgo > 0) return `${minutesAgo}分钟前`
    return '刚刚'
}

/**
 * 千位符金额转数字
 * @param amount 带千位符的金额字符串，如 "1,000,100.00"
 * @returns number，如 1000100.00
 */
export function parseAmount(amount: string): number {
    if (!amount) return 0

    // 去掉所有逗号
    const normalized = amount.replace(/,/g, '')

    // 转成 number
    const num = Number(normalized)

    if (isNaN(num)) {
        throw new Error(`Invalid amount: ${amount}`)
    }

    return num
}

/**
 * 将日期转换为"多久前"格式
 * @param {string|Date} date - 日期字符串(YYYY-MM-DD 或 YYYY-MM-DD HH:MM:SS)或Date对象
 * @returns {string} 相对时间描述
 */
export const formatTimeAgo = (date: any) => {
    // 处理日期输入
    const targetDate =
        typeof date === 'string'
            ? new Date(date.replace(/-/g, '/')) // 处理Safari兼容性
            : date

    // 验证日期有效性
    if (isNaN(targetDate.getTime())) {
        return '无效日期'
    }

    // 计算时间差（秒）
    const now = new Date()
    const diffSeconds = Math.floor((now - targetDate) / 1000)

    // 定义时间单位和对应的秒数
    const timeUnits = [
        { unit: '年', seconds: 365 * 24 * 60 * 60 },
        { unit: '月', seconds: 30 * 24 * 60 * 60 }, // 简化为30天/月
        { unit: '天', seconds: 24 * 60 * 60 },
        { unit: '小时', seconds: 60 * 60 },
        { unit: '分钟', seconds: 60 },
        { unit: '秒', seconds: 1 },
    ]

    // 计算对应的时间单位
    for (const { unit, seconds } of timeUnits) {
        const value = Math.floor(diffSeconds / seconds)
        if (value >= 1) {
            return `${value}${unit}前`
        }
    }

    // 小于1秒的情况
    return '刚刚'
}

/**
 * 复制
 */
export const onCopy = (data: string) => {
    navigator.clipboard
        .writeText(data)
        .then(() => {
            ElMessage.success('复制成功')
        })
        .catch(() => {
            ElMessage.error('复制失败')
        })
}

export const formatNumber = (numStr: any) => {
    // 校验输入是否为字符串且能转换为有效数字
    if (typeof numStr !== 'string') return numStr
    const num = Number(numStr)
    if (isNaN(num)) return numStr // 无效数字则返回原字符串

    // 判断是否为整数（小数部分为0）
    if (num % 1 === 0) {
        return num.toString() // 整数："88.00" → "88"
    } else {
        // 非整数：保留原字符串的小数精度（避免数字转换导致的精度丢失）
        // 例如 "88.01" → "88.01"，"123.4500" → "123.45"（可选去末尾0）
        // return numStr.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '')
        return numStr
    }
}

// 格式化显示（仅显示用）
export const formatAmountDisplay = (value: string) => {
    if (!value) return ''
    // 保留两位小数（不补零）
    const num = value.replace(/[^\d.]/g, '')
    const parts = num.split('.')
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    if (parts.length > 1) {
        return `${intPart}.${parts[1].slice(0, 2)}`
    }
    return intPart
}

// 解析输入（v-model值）
export const parseAmountInput = (value: string) => {
    if (!value) return ''
    // 去除千分位，只保留数字和小数点，最多两位小数
    const clean = value.replace(/,/g, '')
    const match = clean.match(/^\d*(\.?\d{0,2})?/)
    return match?.length ? match[0] : clean
}


/**
 * 判断是否是图片
 * @param fileName
 */
export const isImg = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toUpperCase()
    const imgExtensions = ['JPG','JPEG','PNG','BMP']
    return imgExtensions.includes(extension as string)
}

/**
 * 新窗口打开系统中的任意页面
 */
export const openSysLink = (url: string) => {
    const origin = window.location.origin
    window.open(origin + url, '_blank')
}

/**
 * 返回当前订单的短连接
 * @param type
 */
export const getTypeUrl = (type: string | number) => {
    return type == 1 ? 'LoanOrders' : type == 2 ? 'PurchaseOrders' : 'ytpay.TradeApplyOrders'
}
